import { Member, Role } from '@prisma/client';
import { AddMemberBodyParams, MemberPermission, MemberPermissionType } from '@zond/api-interfaces';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from '@zond/ui';
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAddMember } from '../../members/members.queries';
import { FileUpload } from './components/file-upload';
import { MemberRoleForm } from './components/member-role-form';
import { MultiEmailInput } from './components/multi-email-input';
import { DialogProps, MemberFormData } from './types';
import { isValidEmail, normalizeEmail } from './utils/email-utils';

interface EmailItem {
  value: string;
  isValid: boolean;
}

export const InviteMembersDialog = ({ isOpen, onOpenChange, onSuccess, triggerButtonProps }: DialogProps) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState<MemberFormData & { emails: EmailItem[] }>({
    emails: [],
    role: Role.USER,
    permissions: {
      [MemberPermission.TODO]: { type: MemberPermissionType.Simple, available: false },
    },
  });

  const [isEmailsValid, setIsEmailsValid] = useState(true);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const [showInvalidWarning, setShowInvalidWarning] = useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Clear form when closing
      emailInputRef.current?.blur();
      setFormData({
        emails: [],
        role: Role.USER,
        permissions: {
          [MemberPermission.TODO]: { type: MemberPermissionType.Simple, available: false },
        },
      });
    }
    onOpenChange(open);
  };

  const addMember = useAddMember(
    () => {
      toast.success(t('members.toasts.invitationsSent'));
      handleOpenChange(false);
      onSuccess?.();
    },
    (error) => {
      console.log('Error handler called:', error);
      toast.error(
        t('members.toasts.invitationsFailed', {
          error: error.message || t('members.toasts.unknownError'),
        })
      );
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const invalidEmails = formData.emails.filter((item) => !item.isValid);
    if (invalidEmails.length > 0) {
      setShowInvalidWarning(true);
      return;
    }

    // Get all valid emails
    const validEmails = formData.emails.filter((item) => item.isValid).map((item) => item.value);

    // Send all emails in one request
    addMember.mutate({
      emails: validEmails,
      role: formData.role,
      permissions: formData.permissions,
    });
  };

  const handleEmailsFromFile = (emails: string[]) => {
    setFormData((prev) => {
      // Filter duplicates considering normalization
      const newEmails = emails.filter((email) => {
        const normalizedNew = normalizeEmail(email);
        return !prev.emails.some((item) => normalizeEmail(item.value) === normalizedNew);
      });

      return {
        ...prev,
        emails: [
          ...prev.emails,
          ...newEmails.map((email) => ({
            value: email,
            isValid: isValidEmail(email),
          })),
        ],
      };
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant={triggerButtonProps?.variant || 'default'}
            className={triggerButtonProps?.className}
          >
            {triggerButtonProps?.children || (
              <>
                <Plus className="mr-2 h-4 w-4" />
                {t('members.inviteDialog.trigger')}
              </>
            )}
          </Button>
        </DialogTrigger>
        <DialogContent
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            emailInputRef.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('members.inviteDialog.title')}</DialogTitle>
            <DialogDescription>{t('members.inviteDialog.description')}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('members.inviteDialog.emailLabel')}</Label>
              <div className="space-y-1">
                <MultiEmailInput
                  id="email"
                  ref={emailInputRef}
                  value={formData.emails}
                  onChange={(emails) => {
                    setFormData((prev) => ({ ...prev, emails }));
                    setShowInvalidWarning(false);
                  }}
                  onValidityChange={setIsEmailsValid}
                  placeholder={t('members.inviteDialog.emailPlaceholder')}
                />

                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span>{t('members.inviteDialog.or')} </span>
                    <FileUpload onEmailsExtracted={handleEmailsFromFile} className="inline-block" />
                  </div>
                  {formData.emails.length > 0 && (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-primary hover:underline"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, emails: [] }));
                        setShowInvalidWarning(false);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>{t('members.inviteDialog.clearAll')}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {showInvalidWarning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{t('members.inviteDialog.invalidWarning')}</AlertDescription>
              </Alert>
            )}
            <MemberRoleForm
              value={formData}
              onChange={(newData) => setFormData((prev) => ({ ...prev, ...newData }))}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={formData.emails.length === 0 || addMember.isPending}
            >
              {addMember.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('members.inviteDialog.submit')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
